/**
 * Status display component for Generate Assessment Plan page
 *
 * Compact iframe-appropriate UI with 4 states:
 * - Initializing (loading)
 * - Waiting for dxList
 * - Generating (processing spinner)
 * - Error
 */

import { CompactErrorState } from '@/components/PublicRecording/CompactErrorState';
import { CompactLoadingState } from '@/components/PublicRecording/CompactLoadingState';
import { PulsingDots } from '@/components/PublicRecording/PulsingDots';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

interface GenerateAssessmentPlanStatusProps {
  isValidating: boolean;
  isValidated: boolean;
  scribeJobId: string | null;
  hasDxList: boolean;
  isGenerating: boolean;
  error: string | null;
}

/**
 * Generating state icon — sparkle with orbital ring and shimmer
 * Matches the CompactUploadingState processing phase visual style
 */
const GeneratingIcon = () => (
  <div className="relative w-11 h-11 flex items-center justify-center">
    {/* Orbital ring */}
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-dashed border-purple-300 dark:border-purple-700"
      animate={{ rotate: 360 }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
    />

    {/* Inner circle with shimmer */}
    <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/40 dark:to-indigo-900/40 flex items-center justify-center overflow-hidden border border-purple-200/60 dark:border-purple-700/60">
      <motion.div
        animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
        <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
      </motion.div>

      {/* Shimmer sweep */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 dark:via-white/10 to-transparent"
        animate={{ x: ['-100%', '100%'] }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut',
          repeatDelay: 0.6,
        }}
      />
    </div>
  </div>
);

/**
 * Waiting for data state — pulsing dots with message
 */
const WaitingForDataState = () => (
  <motion.div
    key="waiting"
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    style={{ maxHeight: '48px' }}
    className="relative w-full h-full flex items-center justify-center px-3">
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          className="flex items-center gap-3 cursor-help focus:outline-none"
          type="button"
          aria-label="Waiting for diagnosis data">
          <div className="w-11 h-11 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/60 dark:to-indigo-950/60 flex items-center justify-center border border-purple-200/50 dark:border-purple-800/50">
              <PulsingDots count={3} size={4} />
            </div>
          </div>
          <motion.span
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="text-xs text-purple-600 dark:text-purple-400 font-medium">
            Waiting for data...
          </motion.span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={8} avoidCollisions={false} className="z-50">
        <p>Waiting for diagnosis data from parent application...</p>
      </TooltipContent>
    </Tooltip>
  </motion.div>
);

/**
 * Generating state — processing spinner with "Generating..." label
 */
const GeneratingState = () => (
  <motion.div
    key="generating"
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    style={{ maxHeight: '48px' }}
    className="relative w-full h-full flex items-center justify-center px-3">
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          className="flex items-center gap-3 cursor-help focus:outline-none"
          type="button"
          aria-label="Generating assessment and plan">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}>
            <GeneratingIcon />
          </motion.div>
          <motion.span
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="text-xs text-purple-600 dark:text-purple-400 font-medium">
            Generating...
          </motion.span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={8} avoidCollisions={false} className="z-50">
        <p>Generating assessment and plan...</p>
      </TooltipContent>
    </Tooltip>
  </motion.div>
);

export const GenerateAssessmentPlanStatus = ({
  isValidating,
  isValidated,
  scribeJobId,
  hasDxList,
  isGenerating,
  error,
}: GenerateAssessmentPlanStatusProps) => {
  const getStateKey = () => {
    if (isValidating) return 'loading';
    if (error || !isValidated || !scribeJobId) return 'error';
    if (isGenerating || hasDxList) return 'generating';
    return 'waiting';
  };

  const stateKey = getStateKey();

  return (
    <div className="flex items-center justify-center h-full">
      <AnimatePresence mode="wait">
        {stateKey === 'loading' && <CompactLoadingState key="loading" />}
        {stateKey === 'error' && <CompactErrorState key="error" error={error} />}
        {stateKey === 'generating' && <GeneratingState key="generating" />}
        {stateKey === 'waiting' && <WaitingForDataState key="waiting" />}
      </AnimatePresence>
    </div>
  );
};
